import axios from 'axios'
import type { ManifestResponse } from '../types/manifest'
import type {
  WikiTree,
  WikiPageDetail,
  WikiShelf,
  PageByPathInfo,
  WikiTemplate,
  WikiMedia,
  WikiRevision,
  WikiRevisionDetail,
} from '../types/wiki'
import { encodeDocPath } from '../lib/paths'

const API = '/api'

axios.defaults.withCredentials = true

let onUnauthorized: (() => void) | null = null
let csrfToken: string | null = null

/** Called once from AuthProvider: session invalid → clear UI and go to login. */
export function registerUnauthorized(handler: (() => void) | null): void {
  onUnauthorized = handler
}

export function clearCsrfToken(): void {
  csrfToken = null
}

export async function fetchCsrfToken(): Promise<string> {
  const { data } = await axios.get<{ token: string }>(`${API}/wiki/csrf-token`)
  csrfToken = data.token
  return data.token
}

async function ensureCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken
  return fetchCsrfToken()
}

function csrfHeaders(token: string): Record<string, string> {
  return { 'X-CSRF-Token': token }
}

axios.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      const url = String((err.config as { url?: string } | undefined)?.url || '')
      // csrf-token is fetched right after login; a 401 here means the session
      // cookie did not stick — do not treat it as a logout (that hard-bounced
      // the SPA back to the login screen). Login/session probes are expected 401s.
      if (
        url.includes('/auth/login') ||
        url.includes('/auth/session') ||
        url.includes('/wiki/csrf-token')
      ) {
        return Promise.reject(err)
      }
      onUnauthorized?.()
    }
    return Promise.reject(err)
  }
)

export type UserRole = 'user' | 'admin' | 'editor' | 'viewer' | 'editor' | 'viewer'

export interface SessionProbe {
  authenticated: boolean
  username?: string
  role?: UserRole
  expiresIn?: number
}

export async function probeSession(): Promise<SessionProbe> {
  try {
    const { data } = await axios.get<SessionProbe>(`${API}/auth/session`)
    return data
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.status === 401) {
      return { authenticated: false }
    }
    throw err
  }
}

export async function login(email: string, password: string): Promise<{
  expiresIn: number
  username: string
  role: UserRole
}> {
  const { data } = await axios.post(`${API}/auth/login`, { email, password })
  return data
}

export async function logout(): Promise<void> {
  await axios.post(`${API}/auth/logout`)
}

export async function fetchConfig(): Promise<{ conventionsPath: string }> {
  const { data } = await axios.get(`${API}/config`)
  return data
}

export async function fetchExists(relPath: string): Promise<{ exists: boolean; path: string }> {
  const { data } = await axios.get(`${API}/exists`, { params: { path: relPath } })
  return data
}

export async function fetchManifest(): Promise<ManifestResponse> {
  const { data } = await axios.get<ManifestResponse>(`${API}/manifest`)
  return data
}

export async function fetchDocMarkdown(relPath: string): Promise<string> {
  const { data } = await axios.get<string>(`${API}/doc/${encodeDocPath(relPath)}`, {
    responseType: 'text',
  })
  return typeof data === 'string' ? data : String(data)
}

export async function saveDocumentMarkdown(
  relPath: string,
  markdown: string,
  password: string
): Promise<{ ok: boolean; path: string }> {
  const token = await ensureCsrfToken()
  const { data } = await axios.put<{ ok: boolean; path: string }>(
    `${API}/doc/${encodeDocPath(relPath)}`,
    { markdown, password },
    { headers: csrfHeaders(token) }
  )
  return data
}

export interface SearchHit {
  path: string
  title: string
  score: number
  slug?: string
}

export interface SearchFilters {
  shelfSlug?: string
  bookSlug?: string
  tag?: string
}

export async function search(
  q: string,
  limit = 20,
  filters?: SearchFilters
): Promise<{ query: string; hits: SearchHit[] }> {
  const params: Record<string, string | number> = { q, limit }
  if (filters?.shelfSlug) params.shelfSlug = filters.shelfSlug
  if (filters?.bookSlug) params.bookSlug = filters.bookSlug
  if (filters?.tag) params.tag = filters.tag
  const { data } = await axios.get(`${API}/search`, { params })
  return data
}

export function downloadDocUrl(relPath: string): string {
  return `${API}/download/${encodeDocPath(relPath)}`
}

export async function uploadMarkdown(
  file: File,
  relPath: string,
  force = false
): Promise<{ ok: boolean; path: string }> {
  const form = new FormData()
  form.append('file', file)
  form.append('relPath', relPath)
  form.append('force', force ? 'true' : 'false')
  const token = await ensureCsrfToken()
  const { data } = await axios.post<{ ok: boolean; path: string }>(`${API}/upload`, form, {
    headers: csrfHeaders(token),
  })
  return data
}

export async function uploadZip(file: File, prefix: string, force = false): Promise<{
  ok: boolean
  mdPaths: string[]
  assetPaths: string[]
}> {
  const form = new FormData()
  form.append('file', file)
  form.append('prefix', prefix)
  form.append('force', force ? 'true' : 'false')
  const token = await ensureCsrfToken()
  const { data } = await axios.post(`${API}/upload-zip`, form, { headers: csrfHeaders(token) })
  return data
}

export async function deleteDocument(relPath: string, password: string): Promise<{ ok: boolean; path: string }> {
  const token = await ensureCsrfToken()
  const { data } = await axios.post<{ ok: boolean; path: string }>(
    `${API}/delete`,
    { path: relPath, password },
    { headers: csrfHeaders(token) }
  )
  return data
}

export interface AuditEntry {
  action?: string
  path?: string
  username?: string
  at?: string
  source?: string
}

export async function fetchAudit(params: {
  limit?: number
  path?: string
  action?: string
}): Promise<{ entries: AuditEntry[] }> {
  const { data } = await axios.get(`${API}/audit`, { params })
  return data
}

export async function fetchWikiTree(): Promise<WikiTree> {
  const { data } = await axios.get<WikiTree>(`${API}/wiki/tree`)
  return data
}

export async function fetchWikiShelves(): Promise<{ shelves: WikiShelf[] }> {
  const { data } = await axios.get<{ shelves: WikiShelf[] }>(`${API}/wiki/shelves`)
  return data
}

export async function fetchWikiPage(slug: string): Promise<WikiPageDetail> {
  const { data } = await axios.get<WikiPageDetail>(`${API}/wiki/pages/${encodeURIComponent(slug)}`)
  return data
}

export async function fetchWikiPageByPath(path: string): Promise<PageByPathInfo> {
  try {
    const { data } = await axios.get<PageByPathInfo>(`${API}/wiki/page-by-path`, { params: { path } })
    return data
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.status === 503) {
      return { linked: false }
    }
    throw err
  }
}

async function wikiMutation<T>(
  method: 'post' | 'put' | 'delete' | 'patch',
  url: string,
  body?: unknown
): Promise<T> {
  const token = await ensureCsrfToken()
  const { data } = await axios.request<T>({
    method,
    url,
    data: body,
    headers: csrfHeaders(token),
  })
  return data
}

export async function createWikiShelf(input: {
  name: string
  slug: string
  description?: string
  sort_order?: number
}): Promise<WikiShelf> {
  return wikiMutation('post', `${API}/wiki/shelves`, input)
}

export async function updateWikiShelf(
  slug: string,
  input: { name?: string; slug?: string; description?: string | null; sort_order?: number }
): Promise<WikiShelf> {
  return wikiMutation('put', `${API}/wiki/shelves/${encodeURIComponent(slug)}`, input)
}

export async function deleteWikiShelf(slug: string): Promise<{ ok: boolean }> {
  return wikiMutation('delete', `${API}/wiki/shelves/${encodeURIComponent(slug)}`)
}

export async function bootstrapWiki(): Promise<{
  ok: boolean
  shelves: number
  books: number
  chapters: number
  pages: number
}> {
  return wikiMutation('post', `${API}/wiki/bootstrap`)
}

export async function fetchWikiTemplates(): Promise<{ templates: WikiTemplate[] }> {
  const { data } = await axios.get<{ templates: WikiTemplate[] }>(`${API}/wiki/templates`)
  return data
}

export async function fetchWikiMedia(): Promise<{ media: WikiMedia[] }> {
  const { data } = await axios.get<{ media: WikiMedia[] }>(`${API}/wiki/media`)
  return data
}

export async function uploadWikiMedia(file: File): Promise<WikiMedia> {
  const form = new FormData()
  form.append('file', file)
  const token = await ensureCsrfToken()
  const { data } = await axios.post<WikiMedia>(`${API}/wiki/media`, form, {
    headers: csrfHeaders(token),
  })
  return data
}

export async function createWikiPage(input: {
  chapter_slug: string
  title: string
  slug: string
  corpus_path: string
  published?: boolean
}): Promise<WikiPageDetail> {
  return wikiMutation('post', `${API}/wiki/pages`, input)
}

export async function saveWikiPageContent(
  slug: string,
  input: { markdown?: string; html?: string; password: string }
): Promise<{ ok: boolean; slug: string }> {
  return wikiMutation('put', `${API}/wiki/pages/${encodeURIComponent(slug)}/content`, input)
}

export async function patchWikiPage(
  slug: string,
  input: { published?: boolean; tags?: string[] }
): Promise<WikiPageDetail> {
  return wikiMutation('patch', `${API}/wiki/pages/${encodeURIComponent(slug)}`, input)
}

export async function fetchPageRevisions(slug: string): Promise<{ revisions: WikiRevision[] }> {
  const { data } = await axios.get<{ revisions: WikiRevision[] }>(
    `${API}/wiki/pages/${encodeURIComponent(slug)}/revisions`
  )
  return data
}

export async function fetchRevision(slug: string, revisionId: string): Promise<WikiRevisionDetail> {
  const { data } = await axios.get<WikiRevisionDetail>(
    `${API}/wiki/pages/${encodeURIComponent(slug)}/revisions/${encodeURIComponent(revisionId)}`
  )
  return data
}

export async function restoreRevision(
  slug: string,
  revisionId: string,
  password: string
): Promise<{ ok: boolean; slug: string }> {
  return wikiMutation(
    'post',
    `${API}/wiki/pages/${encodeURIComponent(slug)}/revisions/${encodeURIComponent(revisionId)}/restore`,
    { password }
  )
}

// ---------------------------------------------------------------------------
// Admin API — user management, permissions, pending changes
// ---------------------------------------------------------------------------

export interface AdminUser {
  id: string
  username: string
  role: string
  created_at: string
  last_login?: string
}

export interface AdminGrant {
  id: string
  shelf_slug?: string | null
  book_path?: string | null
  username: string
  permission: string
}

export interface AdminPendingChange {
  id: string
  relative_path: string
  title: string
  action: string
  author: string
  created_at: string
}

export interface DiffResult {
  currentMarkdown: string | null
  pendingMarkdown: string
}

export interface PublishResult {
  committed: number
  commitHash: string | null
}

/** List all users (admin only). */
export async function adminListUsers(): Promise<AdminUser[]> {
  const { data } = await axios.get<AdminUser[]>(`${API}/wiki/admin/users`)
  return data
}

/** Create a new user. Returns { username, role }. Send without id — server generates. */
export async function adminCreateUser(input: {
  username: string
  password: string
  role: string
}): Promise<{ username: string; role: string; id?: string }> {
  const token = await ensureCsrfToken()
  const { data } = await axios.post(`${API}/wiki/admin/users`, input, { headers: csrfHeaders(token) })
  return data
}

/** Update a user's role and/or password. */
export async function adminUpdateUser(
  username: string,
  input: { role?: string; password?: string }
): Promise<{ username: string; role: string }> {
  const token = await ensureCsrfToken()
  const { data } = await axios.patch(`${API}/wiki/admin/users/${encodeURIComponent(username)}`, input, {
    headers: csrfHeaders(token),
  })
  return data
}

/** Delete a user by username. Cannot delete self. */
export async function adminDeleteUser(username: string): Promise<{ ok: boolean }> {
  const token = await ensureCsrfToken()
  const { data } = await axios.delete(`${API}/wiki/admin/users/${encodeURIComponent(username)}`, {
    headers: csrfHeaders(token),
  })
  return data
}

/** List all permission grants (shelf and book). */
export async function adminListGrants(): Promise<AdminGrant[]> {
  const { data } = await axios.get<AdminGrant[]>(`${API}/wiki/admin/grants`)
  return data
}

/** Create a new grant. */
export async function adminCreateGrant(input: {
  shelf_slug?: string
  book_path?: string
  username: string
  permission: string
}): Promise<AdminGrant> {
  const token = await ensureCsrfToken()
  const { data } = await axios.post(`${API}/wiki/admin/grants`, input, { headers: csrfHeaders(token) })
  return data
}

/** Update a grant's permission level. */
export async function adminUpdateGrant(
  id: string,
  input: { permission: string }
): Promise<AdminGrant> {
  const token = await ensureCsrfToken()
  const { data } = await axios.patch(`${API}/wiki/admin/grants/${encodeURIComponent(id)}`, input, {
    headers: csrfHeaders(token),
  })
  return data
}

/** Delete a grant. */
export async function adminDeleteGrant(id: string): Promise<{ ok: boolean }> {
  const token = await ensureCsrfToken()
  const { data } = await axios.delete(`${API}/wiki/admin/grants/${encodeURIComponent(id)}`, {
    headers: csrfHeaders(token),
  })
  return data
}

/** List all pending changes awaiting publish. */
export async function adminListPending(): Promise<AdminPendingChange[]> {
  const { data } = await axios.get<AdminPendingChange[]>(`${API}/wiki/admin/pending`)
  return data
}

/** Get diff preview for a pending change. */
export async function adminGetPendingDiff(id: string): Promise<{
  currentMarkdown: string | null
  pendingMarkdown: string
}> {
  const { data } = await axios.get(`${API}/wiki/admin/pending/${encodeURIComponent(id)}/diff`)
  return data
}

/** Discard a single pending change without publishing. */
export async function adminDiscardPending(id: string): Promise<{ ok: boolean }> {
  const token = await ensureCsrfToken()
  const { data } = await axios.delete(`${API}/wiki/admin/pending/${encodeURIComponent(id)}`, {
    headers: csrfHeaders(token),
  })
  return data
}

/** Publish all pending changes to filesystem + git commit. */
export async function adminPublishAll(): Promise<PublishResult> {
  const token = await ensureCsrfToken()
  const { data } = await axios.post(`${API}/wiki/admin/publish`, {}, { headers: csrfHeaders(token) })
  return data
}
